import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { useRequireAuth } from "../hooks/useRequireAuth";
export default function Tutorial() {
  useRequireAuth()
  const { name } = useParams();
  const [status, setStatus] = useState(null);

  const pages = [
    { id: "home", title: "Strona główna" },
    { id: "galeria", title: "Galeria" },
    { id: "kontakt", title: "Kontakt" },
    { id: "oferta", title: "Oferta" },
  ];

  const [selectedPageId, setSelectedPageId] = useState("home");
  const [title] = useState("Moja Restauracja");

  const [sekcjaPowitalna, setSekcjaPowitalna] = useState({
    naglowek: "Witamy w naszej restauracji",
    tekst: "Najlepsze smaki włoskiej kuchni w sercu miasta",
    przyciskTekst: "Zarezerwuj stolik",
    przyciskLink: "/rezerwacja",
    obraz: "",
  });

  const [sections, setSections] = useState([
    {
      type: "text",
      order: 0,
      title: "O nas",
      text: "Jesteśmy rodzinną restauracją z tradycjami od 20 lat. Specjalizujemy się w autentycznej kuchni włoskiej, przygotowywanej z najświeższych składników.",
      image: "",
      imagePosition: "left",
    },
    {
      type: "cta",
      order: 1,
      title: "Zarezerwuj stolik już dziś!",
      text: "Nie czekaj – najlepsze miejsca szybko się rozchodzą.",
      cta: { text: "Zarezerwuj teraz", link: "/rezerwacja" },
    },
  ]);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Markdown do podglądu
  const [markdownExample, setMarkdownExample] = useState(`# Przykładowy nagłówek

To jest **pogrubiony** tekst, a to *kursywa*.

## Lista dań:
- Pizza Margherita
- Spaghetti Carbonara
- Tiramisu

> Cytat: „Najlepsza pizza w mieście!”

[Link do rezerwacji](/rezerwacja)`);

  const addTextSection = () => {
    setSections(prev => [...prev, {
      type: "text",
      order: prev.length,
      title: "Nowa sekcja tekstowa",
      text: "",
      image: "",
      imagePosition: "left",
    }]);
  };

  const addTilesSection = () => {
    setSections(prev => [...prev, {
      type: "tiles",
      order: prev.length,
      title: "Nasza oferta",
      items: [
        { title: "Pizza", text: "Tradycyjna włoska", image: "", cta: { text: "Zobacz menu", link: "/menu" } },
        { title: "Makaron", text: "Świeżo robiony", image: "", cta: { text: "Zobacz menu", link: "/menu" } },
      ],
    }]);
  };

  const addCtaSection = () => {
    setSections(prev => [...prev, {
      type: "cta",
      order: prev.length,
      title: "Skontaktuj się z nami",
      text: "Masz pytania? Chętnie pomożemy!",
      cta: { text: "Zadzwoń", link: "tel:+48123456789" },
    }]);
  };

  const addTile = (sectionIndex) => {
    setSections(prev => prev.map((sec, i) => {
      if (i !== sectionIndex || sec.type !== "tiles") return sec;
      return {
        ...sec,
        items: [...sec.items, {
          title: "Nowy kafelek",
          text: "Opis nowego elementu",
          image: "",
          cta: { text: "Dowiedz się więcej", link: "/" },
        }],
      };
    }));
  };

  const updateSection = (idx, field, value) => {
    setSections(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const updateTile = (sectionIdx, tileIdx, field, value) => {
    setSections(prev => prev.map((sec, i) => {
      if (i !== sectionIdx || sec.type !== "tiles") return sec;
      const newItems = [...sec.items];
      newItems[tileIdx] = { ...newItems[tileIdx], [field]: value };
      return { ...sec, items: newItems };
    }));
  };

  const removeSection = (idx) => {
    setSections(prev => prev.filter((_, i) => i !== idx));
  };

  const uploadImage = (file, sectionIndex, tileIndex = null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIndex) return s;
      if (tileIndex === null) return { ...s, image: url };
      if (s.type !== "tiles") return s;
      const newItems = s.items.map((it, t) => t === tileIndex ? { ...it, image: url } : it);
      return { ...s, items: newItems };
    }));
  };

  const uploadHeroImage = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSekcjaPowitalna(prev => ({ ...prev, obraz: url }));
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-16 pb-32">
      <div className="mx-4 sm:mx-6 lg:mx-12 space-y-12">
        {/* Krok 1 */}
        <div className="bg-blue-50 border-4 border-blue-600 rounded-2xl p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-blue-900">Krok 1: Wybierz stronę do edycji</h2>
          <p className="text-lg sm:text-xl mb-8 leading-relaxed">
            Na górze panelu administracyjnego masz dwa pola:<br />
            <strong>Lewe</strong> – wybierasz stronę, którą chcesz edytować.<br />
            <strong>Prawe</strong> – dodajesz nową stronę (np. „Menu”, „O nas”).
          </p>

          <div className="bg-white p-6 sm:p-8 shadow-xl rounded-xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-base sm:text-lg font-medium mb-3">Wybierz stronę</label>
                <select
                  value={selectedPageId}
                  onChange={(e) => setSelectedPageId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-5 py-4 text-base sm:text-lg focus:ring-2 focus:ring-blue-500"
                >
                  {pages.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-base sm:text-lg font-medium mb-3">Dodaj nową stronę</label>
                <input placeholder="Np. O nas" className="w-full border border-gray-300 rounded-lg px-5 py-4 mb-4" />
                <button
                  onClick={() => {
                    setStatus({ type: "success", message: "Strona dodana!" });
                    setTimeout(() => setStatus(null), 3000);
                  }}
                  className="w-full bg-emerald-600 text-white py-4 rounded-lg hover:bg-emerald-700 font-medium text-base sm:text-lg"
                >
                  Dodaj stronę
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Krok 2 – Sekcja powitalna */}
        <div className="bg-blue-50 border-4 border-blue-600 rounded-2xl p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-blue-900">Krok 2: Edytuj duże zdjęcie i tekst na górze</h2>
          <p className="text-lg sm:text-xl mb-8 leading-relaxed">
            Ta sekcja pojawia się tylko na stronach: <strong>Strona główna</strong>, <strong>Galeria</strong>, <strong>Kontakt</strong>.<br />
            To najważniejsze miejsce na stronie – klienci widzą je jako pierwsze.
          </p>

          {(selectedPageId === "home" || selectedPageId === "kontakt" || selectedPageId === "galeria") && (
            <div className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-6">Sekcja powitalna</h3>
              <input
                placeholder="Duży nagłówek"
                value={sekcjaPowitalna.naglowek}
                onChange={(e) => setSekcjaPowitalna(p => ({ ...p, naglowek: e.target.value }))}
                className="w-full mb-5 border rounded-lg px-5 py-4 text-xl sm:text-2xl font-semibold focus:ring-2 focus:ring-emerald-500"
              />
              <textarea
                placeholder="Tekst pod nagłówkiem"
                value={sekcjaPowitalna.tekst}
                onChange={(e) => setSekcjaPowitalna(p => ({ ...p, tekst: e.target.value }))}
                rows={4}
                className="w-full mb-6 border rounded-lg px-5 py-4 text-base sm:text-lg resize-none focus:ring-2 focus:ring-emerald-500"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <input
                  placeholder="Tekst przycisku"
                  value={sekcjaPowitalna.przyciskTekst}
                  onChange={(e) => setSekcjaPowitalna(p => ({ ...p, przyciskTekst: e.target.value }))}
                  className="border rounded-lg px-5 py-4 focus:ring-2 focus:ring-emerald-500"
                />
                <input
                  placeholder="Link (np. /rezerwacja)"
                  value={sekcjaPowitalna.przyciskLink}
                  onChange={(e) => setSekcjaPowitalna(p => ({ ...p, przyciskLink: e.target.value }))}
                  className="border rounded-lg px-5 py-4 focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <label className="block text-base sm:text-lg font-medium mb-3">Zdjęcie w tle</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => uploadHeroImage(e.target.files?.[0])}
                className="text-base sm:text-lg block w-full file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white"
              />
              {sekcjaPowitalna.obraz && (
                <img src={sekcjaPowitalna.obraz} alt="Podgląd tła" className="mt-6 w-full max-h-80 object-cover rounded-xl shadow-xl" />
              )}
            </div>
          )}
        </div>

        {/* NOWA SEKCJA: Poradnik Markdown + Live Preview */}
        <div className="bg-purple-50 border-4 border-purple-600 rounded-2xl p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-purple-900">Jak używać formatowania tekstu (Markdown)?</h2>
          <p className="text-lg sm:text-xl mb-8 leading-relaxed">
            W miejscach, gdzie wpisujesz dłuższy tekst (np. „O nas”, opisy w kafelkach), możesz używać <strong>Markdown</strong> – prostego formatowania.<br />
            Dzięki temu tekst będzie ładniejszy: nagłówki, listy, pogrubienie, linki, cytaty. Możesz też klikać przyciski nad edytorem, aby dodawać formatowanie automatycznie tak jak w edytorach tekstu jak word.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Wpisz tekst tutaj:</h3>
              <div data-color-mode="light">
                <MDEditor
                  value={markdownExample}
                  onChange={setMarkdownExample}
                  height={400}
                  preview="edit"
                />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Tak będzie wyglądał na stronie:</h3>
              <div className="bg-white p-8 rounded-xl shadow-xl border min-h-96" data-color-mode="light">
                <MDEditor.Markdown source={markdownExample}  />
              </div>
            </div>
          </div>

          <div className="mt-10 bg-white p-6 rounded-xl shadow">
            <h3 className="text-xl font-bold mb-4">Najważniejsze komendy Markdown:</h3>
            <ul className="space-y-3 text-base sm:text-lg">
              <li><code className="bg-gray-200 px-2 py-1 rounded"># Nagłówek</code> → duży tytuł</li>
              <li><code className="bg-gray-200 px-2 py-1 rounded">## Mniejszy nagłówek</code></li>
              <li><strong>**pogrubienie**</strong> → <code>**tekst**</code></li>
              <li><em>*kursywa*</em> → <code>*tekst*</code></li>
              <li><code>- element listy</code> → lista punktowana</li>
              <li><code>{">"} cytat</code> → wyróżniony cytat</li>
              <li><code>[tekst linku](https://...)</code> → klikalny link</li>
            </ul>
          </div>
        </div>

        {/* Krok 3 – Sekcje treści */}
        <div className="bg-blue-50 border-4 border-blue-600 rounded-2xl p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-blue-900">Krok 3: Dodawaj i edytuj treści strony</h2>
          <p className="text-lg sm:text-xl mb-8 leading-relaxed">
            Poniżej masz przykładowe sekcje. Możesz je edytować, usuwać i dodawać nowe.
          </p>

          <div className="bg-white rounded-xl shadow-xl p-6 sm:p-8 space-y-10">
            {sections.map((s, idx) => (
              <div key={idx} className="border border-gray-300 rounded-xl p-6 sm:p-8 bg-gray-50 relative">
                <button
                  onClick={() => removeSection(idx)}
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 text-red-600 hover:text-red-800 font-medium text-base sm:text-lg"
                >
                  Usuń sekcję
                </button>

                <input
                  placeholder="Tytuł sekcji"
                  value={s.title || ""}
                  onChange={(e) => updateSection(idx, "title", e.target.value)}
                  className="w-full mb-6 border rounded-lg px-5 py-4 text-xl sm:text-2xl font-bold focus:ring-2 focus:ring-blue-500"
                />

                {s.type === "tiles" ? (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {s.items?.map((item, tIdx) => (
                        <div key={tIdx} className="bg-white rounded-xl p-6 shadow-lg border">
                          <input
                            placeholder="Tytuł kafelka"
                            value={item.title || ""}
                            onChange={(e) => updateTile(idx, tIdx, "title", e.target.value)}
                            className="w-full mb-4 border rounded-lg px-4 py-3 font-medium text-base sm:text-lg"
                          />
                          <textarea
                            rows={3}
                            value={item.text || ""}
                            onChange={(e) => updateTile(idx, tIdx, "text", e.target.value)}
                            className="w-full mb-4 border rounded-lg px-4 py-3 text-base resize-none"
                          />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => uploadImage(e.target.files?.[0], idx, tIdx)}
                            className="text-sm sm:text-base mb-3 block w-full"
                          />
                          {item.image && <img src={item.image} alt="kafelek" className="w-full h-48 object-cover rounded-lg shadow" />}
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 text-center">
                      <button
                        onClick={() => addTile(idx)}
                        className="bg-emerald-600 text-white px-8 py-4 rounded-xl hover:bg-emerald-700 font-medium text-base sm:text-lg"
                      >
                        + Dodaj kafelek
                      </button>
                    </div>
                  </div>
                ) : s.type === "cta" ? (
                  <div className="space-y-6">
                    <textarea
                      rows={4}
                      value={s.text || ""}
                      onChange={(e) => updateSection(idx, "text", e.target.value)}
                      className="w-full border rounded-lg px-5 py-4 text-base sm:text-lg resize-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input
                        placeholder="Tekst przycisku"
                        value={s.cta?.text || ""}
                        onChange={(e) => updateSection(idx, "cta", { ...s.cta, text: e.target.value })}
                        className="border rounded-lg px-5 py-4 focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        placeholder="Link przycisku"
                        value={s.cta?.link || ""}
                        onChange={(e) => updateSection(idx, "cta", { ...s.cta, link: e.target.value })}
                        className="border rounded-lg px-5 py-4 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <textarea
                      rows={6}
                      value={s.text || ""}
                      onChange={(e) => updateSection(idx, "text", e.target.value)}
                      className="w-full border rounded-lg px-5 py-4 text-base sm:text-lg resize-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadImage(e.target.files?.[0], idx)}
                      className="text-base sm:text-lg block w-full"
                    />
                    {s.image && <img src={s.image} alt="Podgląd" className="w-full max-h-96 object-cover rounded-xl shadow-xl" />}
                  </div>
                )}
              </div>
            ))}

            <div className="flex flex-wrap gap-4 justify-center pt-8">
              <button onClick={addTextSection} className="flex-1 sm:flex-initial bg-gray-200 hover:bg-gray-300 px-6 py-4 rounded-xl font-medium text-base sm:text-lg">
                + Sekcja tekstowa
              </button>
              <button onClick={addTilesSection} className="flex-1 sm:flex-initial bg-gray-200 hover:bg-gray-300 px-6 py-4 rounded-xl font-medium text-base sm:text-lg">
                + Sekcja kafelków
              </button>
              <button onClick={addCtaSection} className="flex-1 sm:flex-initial bg-emerald-600 text-white hover:bg-emerald-700 px-6 py-4 rounded-xl font-medium text-base sm:text-lg">
                + Sekcja z przyciskiem
              </button>
            </div>
          </div>
        </div>

        {/* Krok 4 – Podgląd */}
        <div className="bg-blue-50 border-4 border-blue-600 rounded-2xl p-6 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-blue-900">Krok 4: Zawsze sprawdzaj podgląd!</h2>
          <p className="text-lg sm:text-xl mb-8 max-w-4xl mx-auto">
            Po każdej zmianie kliknij niebieski przycisk w prawym dolnym rogu: <strong>„Podgląd”</strong><br />
            Zobaczysz stronę dokładnie tak, jak widzą ją klienci.
          </p>
        </div>

        {/* Powrót do panelu */}
        <div className="bg-white rounded-2xl p-8 sm:p-10 text-center shadow-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Gotowy? Przejdź do prawdziwego panelu!</h2>
          <p className="text-lg sm:text-xl mb-8">Ten poradnik to tylko symulacja – nic się tu nie zapisuje.</p>
          <Link
            to={`/${name}/admin`}
            className="inline-block bg-blue-600 text-white px-10 py-6 rounded-2xl text-xl sm:text-2xl font-bold hover:bg-blue-700 shadow-xl"
          >
            Przejdź do prawdziwego panelu administracyjnego →
          </Link>
          <p className="text-2xl sm:text-3xl font-bold text-green-700 mt-10">Powodzenia!</p>
        </div>
      </div>

      {/* Przycisk podglądu */}
      <button
        onClick={() => setIsPreviewOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-blue-600 text-white px-6 py-4 rounded-full shadow-2xl text-base sm:text-lg font-semibold hover:bg-blue-700"
      >
        Podgląd
      </button>

      {/* Modal podglądu – bez zmian, działa dobrze */}
      {isPreviewOpen && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
          <div className="relative w-full h-full max-w-6xl bg-white rounded-2xl overflow-hidden shadow-2xl">
            <button
              onClick={() => setIsPreviewOpen(false)}
              className="absolute top-4 right-4 z-10 bg-red-600 text-white w-12 h-12 rounded-full hover:bg-red-700 flex items-center justify-center text-3xl"
            >
              ×
            </button>
            <div className="bg-gray-900 text-white p-8 text-center">
              <h1 className="text-4xl sm:text-5xl font-bold">{title}</h1>
            </div>
            <div className="overflow-y-auto h-full p-6 sm:p-12 space-y-16 bg-gray-50">
              {(selectedPageId === "home" || selectedPageId === "kontakt" || selectedPageId === "galeria") && sekcjaPowitalna.obraz && (
                <div
                  className="relative h-80 sm:h-96 rounded-3xl overflow-hidden shadow-2xl"
                  style={{ backgroundImage: `url(${sekcjaPowitalna.obraz})`, backgroundSize: "cover", backgroundPosition: "center" }}
                >
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-center text-white p-8">
                    <div>
                      <h2 className="text-4xl sm:text-6xl font-bold mb-6">{sekcjaPowitalna.naglowek}</h2>
                      <p className="text-xl sm:text-2xl mb-8">{sekcjaPowitalna.tekst}</p>
                      {sekcjaPowitalna.przyciskTekst && (
                        <a href={sekcjaPowitalna.przyciskLink} className="bg-emerald-600 px-8 py-4 rounded-full text-xl sm:text-2xl font-bold hover:bg-emerald-700">
                          {sekcjaPowitalna.przyciskTekst}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {sections.map((s, i) => (
                <section key={i} className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl">
                  <h3 className="text-3xl sm:text-4xl font-bold mb-8 text-center">{s.title}</h3>
                  {s.type === "tiles" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {s.items?.map((item, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-2xl p-8 text-center shadow-lg">
                          {item.image && <img src={item.image} className="w-full h-64 object-cover rounded-xl mb-6" alt={item.title} />}
                          <h4 className="text-2xl font-bold mb-4">{item.title}</h4>
                          <p className="text-lg mb-6">{item.text}</p>
                          {item.cta?.text && <a href={item.cta.link} className="inline-block bg-emerald-600 text-white px-6 py-4 rounded-xl hover:bg-emerald-700">{item.cta.text}</a>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className="text-lg sm:text-xl mb-10 whitespace-pre-wrap text-center">{s.text}</p>
                      {s.image && <img src={s.image} className="w-full max-h-96 object-cover rounded-2xl mb-10 shadow-lg mx-auto" alt="sekcja" />}
                      {s.type === "cta" && s.cta?.text && (
                        <div className="text-center">
                          <a href={s.cta.link} className="inline-block bg-emerald-600 text-white px-10 py-6 rounded-2xl text-2xl font-bold hover:bg-emerald-700">
                            {s.cta.text}
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {status && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50">
          <div className={`px-8 py-4 rounded-full shadow-2xl text-white font-bold text-base sm:text-lg ${status.type === "success" ? "bg-emerald-600" : "bg-red-600"}`}>
            {status.message}
          </div>
        </div>
      )}
    </div>
  );
}